// routes/AppRoutes.js
import React, { Suspense, lazy, useMemo, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useRoles } from '../context/RolesContext';
import { modalCleanup } from '../utils/modalCleanup';
import { PageSkeleton } from '../components/skeletons';
import FeatureErrorBoundary from '../components/common/FeatureErrorBoundary';
import MdmLegacyRedirect from './MdmLegacyRedirect';

// Lazy loading de tous les composants
const Home = lazy(() => import('../pages/Home'));
const AllEventsPage = lazy(() => import('../pages/AllEventsPage'));
const EventDetailsPage = lazy(() => import('../pages/EventDetailsPage'));
const CommercialPage = lazy(() => import('../pages/CommercialPage'));
const NewEventPage = lazy(() => import('../pages/NewEventPage'));
const AdminDashboard = lazy(() => import('../pages/AdminDashboard'));

const ResourcesPage = lazy(() => import('../pages/ResourcesPage'));
const AdminResourcesPage = lazy(() => import('../pages/AdminResourcesPage'));
const CompletedEventsPage = lazy(() => import('../pages/CompletedEventsPage'));
const RetexPage = lazy(() => import('../pages/RetexPage'));
const OrganizersPage = lazy(() => import('../features/organizers/OrganizersPage'));
const InvoiceUploadPage = lazy(() => import('../pages/InvoiceUploadPage'));
const AdminInvoiceArchivePage = lazy(() => import('../pages/AdminInvoiceArchivePage'));

// Pages outils
const CsvSeparatorPage = lazy(() => import('../pages/tools/CsvSeparatorPage'));
const CsvConverterPage = lazy(() => import('../pages/tools/CsvConverterPage'));
const AccreditationHubPage = lazy(() => import('../pages/tools/AccreditationHubPage'));
const AccreditationConverterPage = lazy(() => import('../pages/tools/AccreditationConverterPage'));
const AccreditationSparesPage = lazy(() => import('../pages/tools/AccreditationSparesPage'));
const AccreditationTarifsPage = lazy(() => import('../pages/tools/AccreditationTarifsPage'));
const AccreditationProjectsPage = lazy(() => import('../pages/tools/AccreditationProjectsPage'));
const ExcelCompilerPage = lazy(() => import('../pages/tools/ExcelCompilerPage'));
const OrderLookupPage = lazy(() => import('../pages/OrderLookupPage'));
const QRManagementPage = lazy(() => import('../pages/QRManagementPage'));
const PromoCodesPage = lazy(() => import('../pages/PromoCodesPage'));

const TerminalManagementPage = lazy(() => import('../pages/TerminalManagementPage'));
const TerminalMovementsPage = lazy(() => import('../pages/TerminalMovementsPage'));
const DemoPadsPage = lazy(() => import('../pages/DemoPadsPage'));
const TerminalBoxesPage = lazy(() => import('../pages/TerminalBoxesPage'));
const StockAuditSessionsPage = lazy(() => import('../pages/StockAuditSessionsPage'));
const MdmFleetView = lazy(() => import('../features/mdm/MdmFleetView'));
const SupportTicketsPage = lazy(() => import('../pages/SupportTicketsPage'));
const StockPage = lazy(() => import('../features/stock/StockTimelinePage'));
const StockCalendarPage = lazy(() => import('../pages/StockCalendarPage'));
const PlanningPage = lazy(() => import('../pages/PlanningPage'));
const TasksPage = lazy(() => import('../pages/TasksPage'));
const SalesDashboardPage = lazy(() => import('../pages/SalesDashboardPage'));
const SalesDashboardSettingsPage = lazy(() => import('../pages/SalesDashboardSettingsPage'));
const ProspectionHomePage       = lazy(() => import('../pages/prospection/ProspectionHomePage'));
const ProspectionListPage       = lazy(() => import('../pages/prospection/ProspectionListPage'));
const ProspectionSessionPage    = lazy(() => import('../pages/prospection/ProspectionSessionPage'));
const ProspectionTasksPage      = lazy(() => import('../pages/prospection/ProspectionTasksPage'));
const ProspectionIndicatorsPage = lazy(() => import('../pages/prospection/ProspectionIndicatorsPage'));
const ProspectionSettingsPage   = lazy(() => import('../pages/prospection/ProspectionSettingsPage'));
const ProspectionAdminPage      = lazy(() => import('../pages/prospection/ProspectionAdminPage'));


// Skeleton fallback pendant le lazy-load des pages
const LoadingFallback = () => <PageSkeleton />;

// URL externe vers la page suggestions de kube-control (remplace les anciennes pages MongoDB)
const KUBE_CONTROL_SUGGESTIONS_URL = `${
  (typeof window !== 'undefined' && window.env && window.env.kubeControlUrl)
    || process.env.REACT_APP_KUBE_CONTROL_URL
    || 'https://kube-control.prod.wilout-internal.com'
}/suggestions`;

// Redirige vers une URL externe (ouvre dans un nouvel onglet puis retourne a l'accueil).
const ExternalRedirect = ({ url }) => {
  useEffect(() => {
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [url]);
  return <Navigate to="/" replace />;
};

// Nettoyage des résidus de modals Bootstrap
function useModalCleanup() {
  const location = useLocation();

  // Nettoyage au changement de route
  useEffect(() => {
    modalCleanup();
  }, [location.pathname]);

  // Filet de sécurité : nettoyer body et backdrop si aucune modale ouverte
  useEffect(() => {
    const interval = setInterval(modalCleanup, 500);
    return () => clearInterval(interval);
  }, []);
}

const AppRoutes = ({ user, events }) => {
  useModalCleanup();
  const { getRoleByName } = useRoles();

  // Récupérer les permissions du rôle de l'utilisateur
  const userRole = user ? getRoleByName(user.role) : null;
  const userPermissions = useMemo(() => userRole?.permissions || {}, [userRole]);

  // Composant pour les pages à accès refusé
  const AccessDenied = ({ message }) => (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <h2>Accès refusé</h2>
      <p>{message}</p>
    </div>
  );

  // Guard Prospection — évite de répéter le même ternaire 6 fois
  const gateProspection = (element) =>
    userPermissions.canAccessProspection || userPermissions.isAdmin
      ? <FeatureErrorBoundary name="Prospection">{element}</FeatureErrorBoundary>
      : <AccessDenied message="Vous n'avez pas accès au module Prospection." />;

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/evenements" element={<AllEventsPage user={user} />} />
        <Route path="/events/:id" element={<FeatureErrorBoundary name="Details evenement"><EventDetailsPage user={user}/></FeatureErrorBoundary>} />
        <Route
          path="/commercials"
          element={
            user?.teamAutonomous
              ? <Navigate to="/nouvel-evenement" replace />
              : userPermissions.canAccessOnboarding || userPermissions.isAdmin
                ? <CommercialPage />
                : <AccessDenied message="Vous n'avez pas accès à la page commerciale." />
          }
        />
        {/* Équipes autonomes : formulaire simple à la place de l'onboarding */}
        <Route
          path="/nouvel-evenement"
          element={
            userPermissions.canAccessOnboarding || userPermissions.isAdmin
              ? <NewEventPage />
              : <AccessDenied message="Vous n'avez pas le droit de créer des événements." />
          }
        />
        {/* Création d'événement : tout passe par l'onboarding. Anciens liens redirigés. */}
        <Route path="/team-add-event" element={<Navigate to="/commercials" replace />} />
        <Route path="/maroc-add-event" element={<Navigate to="/commercials" replace />} />
        <Route
          path="/ressources"
          element={
            userPermissions.canViewResources || userPermissions.isAdmin
              ? <ResourcesPage />
              : <AccessDenied message="Vous n'avez pas accès au wiki." />
          }
        />
        <Route
          path="/wiki/codes-promo"
          element={
            userPermissions.canViewPromoCodes || userPermissions.isAdmin
              ? <PromoCodesPage />
              : <AccessDenied message="Vous n'avez pas accès aux codes promo." />
          }
        />

        {/* Protection des pages factures basée sur canAccessInvoices */}
        <Route
          path="/documents/factures"
          element={
            userPermissions.canAccessInvoices || userPermissions.isAdmin
              ? <InvoiceUploadPage />
              : <AccessDenied message="Vous n'avez pas accès au dépôt de factures." />
          }
        />
        <Route
          path="/admin/factures"
          element={
            userPermissions.canAccessInvoices || userPermissions.isAdmin
              ? <AdminInvoiceArchivePage />
              : <AccessDenied message="Vous n'avez pas accès aux archives de factures." />
          }
        />

        {/* Routes admin basées sur canAccessAdmin */}
        {(userPermissions.canAccessAdmin || userPermissions.isAdmin) && (
          <>
            <Route path="/admin" element={<FeatureErrorBoundary name="Administration"><AdminDashboard /></FeatureErrorBoundary>} />
            <Route path="/admin/resources" element={<AdminResourcesPage />} />
          </>
        )}

        {/* Route terminaux basée sur canAccessTerminals */}
        {(userPermissions.canAccessTerminals || userPermissions.isAdmin) && (
          <>
            <Route path="/terminals" element={<TerminalManagementPage />} />
            <Route path="/terminals/movements" element={<TerminalMovementsPage />} />
            <Route path="/terminals/boxes" element={<TerminalBoxesPage />} />
            <Route path="/terminals/inventaires" element={<StockAuditSessionsPage />} />
          </>
        )}
        {/* Pads en démo : prêt/retour sans envoi pour les commerciaux (canLoanDemoDevices) */}
        {(userPermissions.canLoanDemoDevices || userPermissions.canEditTerminals || userPermissions.isAdmin) && (
          <Route path="/demo-pads" element={<DemoPadsPage />} />
        )}
        {/* MDM Sunmi — accessible avec canAccessMdmSunmi OU isAdmin */}
        {/* MDM Sunmi : intégré à la page Terminaux (onglet « Parc MDM », fiche par terminal).
            L'ancienne adresse y redirige ; elle reste une page autonome pour qui a le MDM sans
            l'accès aux terminaux. */}
        <Route path="/terminals/mdm" element={
          (userPermissions.canAccessMdmSunmi || userPermissions.isAdmin)
            ? ((userPermissions.canAccessTerminals || userPermissions.isAdmin)
              ? <MdmLegacyRedirect />
              : <FeatureErrorBoundary name="MDM Sunmi"><MdmFleetView /></FeatureErrorBoundary>)
            : <AccessDenied message="Vous n'avez pas acces au MDM Sunmi." />
        } />

        {/* Support Freshdesk — accessible avec canAccessSupport OU isAdmin */}
        <Route path="/support" element={
          (userPermissions.canAccessSupport || userPermissions.isAdmin)
            ? <FeatureErrorBoundary name="Support"><SupportTicketsPage /></FeatureErrorBoundary>
            : <AccessDenied message="Vous n'avez pas acces au support." />
        } />

        <Route
          path="/completed-events"
          element={
            userPermissions.canViewEvents || userPermissions.isAdmin
              ? <CompletedEventsPage />
              : <AccessDenied message="Vous n'avez pas accès aux événements." />
          }
        />
        {/* Annuaire des fiches organisateur : communes à toutes les équipes, events bornés côté API */}
        {['/organisateurs', '/organisateurs/:id'].map((path) => (
          <Route
            key={path}
            path={path}
            element={
              userPermissions.canViewEvents || userPermissions.isAdmin
                ? <FeatureErrorBoundary name="Organisateurs"><OrganizersPage /></FeatureErrorBoundary>
                : <AccessDenied message="Vous n'avez pas accès aux organisateurs." />
            }
          />
        ))}
        <Route
          path="/retex"
          element={
            userPermissions.canAccessRetex || userPermissions.isAdmin
              ? <RetexPage />
              : <AccessDenied message="Vous n'avez pas accès aux Retex." />
          }
        />

        {/* Routes logistique — protégées par canAccessLogistique */}
        <Route path="/logistique/stock" element={
          userPermissions.canAccessLogistique || userPermissions.isAdmin
            ? <StockPage />
            : <AccessDenied message="Vous n'avez pas accès à la logistique." />
        } />
        <Route path="/logistique/stock/calendrier" element={
          userPermissions.canAccessLogistique || userPermissions.isAdmin
            ? <StockCalendarPage />
            : <AccessDenied message="Vous n'avez pas accès à la logistique." />
        } />
        <Route path="/logistique/planning" element={
          userPermissions.canAccessLogistique || userPermissions.canAccessSchedule || userPermissions.isAdmin
            ? <PlanningPage />
            : <AccessDenied message="Vous n'avez pas accès au planning." />
        } />

        {/* Module Prospection — protégé par canAccessProspection */}
        <Route path="/prospection" element={gateProspection(<ProspectionHomePage />)} />
        <Route path="/prospection/lists/:scope/:key" element={gateProspection(<ProspectionListPage />)} />
        <Route path="/prospection/session" element={gateProspection(<ProspectionSessionPage />)} />
        <Route path="/prospection/tasks" element={gateProspection(<ProspectionTasksPage />)} />
        <Route path="/prospection/indicators" element={gateProspection(<ProspectionIndicatorsPage />)} />
        <Route path="/prospection/settings" element={gateProspection(<ProspectionSettingsPage />)} />
        <Route path="/prospection/admin" element={
          userPermissions.isAdmin
            ? <FeatureErrorBoundary name="Prospection Admin"><ProspectionAdminPage /></FeatureErrorBoundary>
            : <AccessDenied message="Accès réservé aux administrateurs." />
        } />

        {/* Dashboard commercial Sellsy — protégé par canAccessSalesDashboard */}
        <Route path="/dashboard-commercial" element={
          userPermissions.canAccessSalesDashboard || userPermissions.isAdmin
            ? <FeatureErrorBoundary name="Dashboard commercial"><SalesDashboardPage /></FeatureErrorBoundary>
            : <AccessDenied message="Vous n'avez pas accès au dashboard commercial." />
        } />
        <Route path="/dashboard-commercial/settings" element={
          userPermissions.canAccessSalesDashboard || userPermissions.isAdmin
            ? <SalesDashboardSettingsPage />
            : <AccessDenied message="Vous n'avez pas accès au dashboard commercial." />
        } />

        {/* Routes outils — protégées par canAccessTools */}
        {(userPermissions.canAccessTools || userPermissions.isAdmin) ? (
          <>
            <Route path="/tools/csv-separator" element={<CsvSeparatorPage />} />
            <Route path="/tools/csv-converter" element={<CsvConverterPage />} />
            <Route path="/tools/accreditation" element={<FeatureErrorBoundary name="Outil"><AccreditationHubPage /></FeatureErrorBoundary>} />
            <Route path="/tools/accreditation-converter" element={<FeatureErrorBoundary name="Outil"><AccreditationConverterPage /></FeatureErrorBoundary>} />
            <Route path="/tools/accreditation-spares" element={<FeatureErrorBoundary name="Outil"><AccreditationSparesPage /></FeatureErrorBoundary>} />
            <Route path="/tools/accreditation-tarifs" element={<FeatureErrorBoundary name="Outil"><AccreditationTarifsPage /></FeatureErrorBoundary>} />
            {(userPermissions.canAccessAccreditation || userPermissions.isAdmin) && (
              <Route path="/tools/accreditation-projects" element={<FeatureErrorBoundary name="Outil"><AccreditationProjectsPage /></FeatureErrorBoundary>} />
            )}
            <Route path="/tools/excel-compiler" element={<FeatureErrorBoundary name="Outil"><ExcelCompilerPage /></FeatureErrorBoundary>} />
            <Route path="/tools/qr-management" element={<QRManagementPage />} />
            {(userPermissions.canAccessOrderSearch || userPermissions.isAdmin) && (
              <Route path="/tools/order-lookup" element={<OrderLookupPage />} />
            )}
          </>
        ) : null}

        {/* Suggestions & demandes de dev : migrees vers kube-control (ouvre dans un nouvel onglet) */}
        <Route path="/suggestions" element={<ExternalRedirect url={KUBE_CONTROL_SUGGESTIONS_URL} />} />
        <Route path="/suggestions/new" element={<ExternalRedirect url={KUBE_CONTROL_SUGGESTIONS_URL} />} />
        <Route path="/demandes-dev" element={<ExternalRedirect url={KUBE_CONTROL_SUGGESTIONS_URL} />} />
        <Route path="/demandes-dev/new" element={<ExternalRedirect url={KUBE_CONTROL_SUGGESTIONS_URL} />} />

        <Route
          path="/taches"
          element={
            userPermissions.canAccessTasks || userPermissions.isAdmin
              ? <TasksPage />
              : <AccessDenied message="Vous n'avez pas accès aux tâches." />
          }
        />
        <Route path="/mes-astreintes" element={<PlanningPage initialTab="astreintes" />} />
        {/* URL inconnue, ou route non déclarée faute de permission */}
        <Route path="*" element={<AccessDenied message="Cette page n'existe pas ou vous n'y avez pas accès." />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
